import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Eye, EyeOff, Trash2, Reply, ShieldCheck, ExternalLink, CheckCheck, AlertTriangle, ThumbsUp, CornerDownRight, Ban, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatWIT } from "@/lib/appApi";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

function BlockedWords() {
  const [words, setWords] = useState(null);
  const [defaultCount, setDefaultCount] = useState(0);
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => { api.get("/comments/blocked-words").then((r) => { setWords(r.data.words); setDefaultCount(r.data.default_count); }).catch(() => setWords([])); }, []);

  const save = async (next) => {
    try { const r = await api.put("/comments/blocked-words", { words: next }); setWords(r.data.words); toast.success("Daftar kata disimpan"); }
    catch { toast.error("Gagal menyimpan"); }
  };
  const add = () => {
    const w = input.trim().toLowerCase();
    if (!w) return;
    if (words.includes(w)) { setInput(""); return; }
    save([...words, w]); setInput("");
  };
  const remove = (w) => save(words.filter((x) => x !== w));

  if (words === null) return null;
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6" data-testid="blocked-words-panel">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2" data-testid="blocked-words-toggle">
        <span className="font-semibold text-dlh-green flex items-center gap-2"><Ban className="h-5 w-5 text-dlh-orange" /> Kata Terlarang Kustom</span>
        <span className="text-xs text-gray-400">{words.length} kata kustom · {defaultCount} bawaan</span>
      </button>
      {open && (
        <div className="mt-4">
          <p className="text-xs text-gray-500 mb-3">Komentar pemirsa yang memuat salah satu kata ini akan otomatis disembunyikan menunggu peninjauan. Daftar bawaan sistem tidak ditampilkan dan tetap aktif.</p>
          <div className="flex gap-2 mb-3">
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="Tambah kata…" className="h-9" data-testid="blocked-word-input" />
            <button onClick={add} data-testid="blocked-word-add" className="inline-flex items-center gap-1 bg-dlh-green text-white text-sm font-semibold px-4 rounded-lg hover:bg-dlh-greenDark"><Plus className="h-4 w-4" /> Tambah</button>
          </div>
          {words.length === 0 ? <p className="text-sm text-gray-400">Belum ada kata kustom.</p> : (
            <div className="flex flex-wrap gap-2" data-testid="blocked-word-list">
              {words.map((w) => (
                <span key={w} className="inline-flex items-center gap-1.5 bg-red-50 text-red-700 text-sm font-medium px-3 py-1 rounded-full border border-red-200">
                  {w}<button onClick={() => remove(w)} data-testid={`blocked-word-remove-${w}`} className="hover:text-red-900"><X className="h-3.5 w-3.5" /></button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const FILTERS = [
  { key: "unreviewed", label: "Perlu Ditinjau" },
  { key: "", label: "Semua" },
  { key: "visible", label: "Tampil" },
  { key: "hidden", label: "Disembunyikan" },
];

export default function Comments() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ visible: 0, hidden: 0, unreviewed: 0 });
  const [filter, setFilter] = useState("unreviewed");
  const [loading, setLoading] = useState(true);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/comments", { params: filter ? { status: filter } : {} })
      .then((r) => { setItems(r.data.items); setCounts(r.data.counts); })
      .catch(() => toast.error("Gagal memuat tanggapan"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (c, status) => {
    try { await api.put(`/comments/${c.id}`, { status }); toast.success(status === "hidden" ? "Tanggapan disembunyikan" : "Tanggapan ditampilkan"); load(); }
    catch { toast.error("Gagal memperbarui"); }
  };
  const markReviewed = async (c) => {
    try { await api.put(`/comments/${c.id}`, { reviewed: true }); load(); }
    catch { toast.error("Gagal"); }
  };
  const markAllReviewed = async () => {
    try { const r = await api.post("/comments/mark-reviewed"); toast.success(`${r.data.updated} tanggapan ditandai sudah ditinjau`); load(); }
    catch { toast.error("Gagal"); }
  };
  const remove = async (c) => {
    if (!window.confirm("Hapus tanggapan ini secara permanen?")) return;
    try { await api.delete(`/comments/${c.id}`); toast.success("Tanggapan dihapus"); load(); }
    catch { toast.error("Gagal menghapus"); }
  };
  const saveReply = async (c) => {
    try { await api.put(`/comments/${c.id}`, { reply: replyText }); toast.success("Tanggapan DLH disimpan"); setReplyFor(null); setReplyText(""); load(); }
    catch { toast.error("Gagal menyimpan tanggapan"); }
  };
  const openReply = (c) => { setReplyFor(c.id); setReplyText(c.reply?.body || ""); };

  return (
    <div data-testid="admin-comments-page">
      <div className="flex items-center gap-2 mb-1">
        <MessageSquare className="h-6 w-6 text-dlh-orange" />
        <h1 className="font-serif text-2xl font-extrabold text-dlh-green">Respon &amp; Tanggapan Pemirsa</h1>
      </div>
      <p className="text-sm text-gray-500 mb-5">{counts.unreviewed} perlu ditinjau · {counts.visible} tampil · {counts.hidden} disembunyikan</p>

      <BlockedWords />

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`comment-filter-${f.key || "all"}`}
            className={`text-sm font-semibold px-4 py-1.5 rounded-full border transition-colors ${filter === f.key ? "bg-dlh-green text-white border-dlh-green" : "bg-white text-gray-600 border-gray-300 hover:border-dlh-green"}`}>
            {f.label}{f.key === "unreviewed" && counts.unreviewed > 0 ? ` (${counts.unreviewed})` : ""}
          </button>
        ))}
        {counts.unreviewed > 0 && (
          <button onClick={markAllReviewed} data-testid="mark-all-reviewed-btn"
            className="ml-auto text-sm font-semibold px-4 py-1.5 rounded-full border border-dlh-greenMid text-dlh-greenMid hover:bg-dlh-greenPale inline-flex items-center gap-1.5">
            <CheckCheck className="h-4 w-4" /> Tandai semua ditinjau
          </button>
        )}
      </div>

      {loading ? <p className="text-gray-500">Memuat…</p> : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center text-gray-500">Tidak ada tanggapan pada tampilan ini.</div>
      ) : (
        <ul className="space-y-4">
          {items.map((c) => (
            <li key={c.id} className={`bg-white rounded-xl border p-4 ${c.status === "hidden" ? "border-amber-300 bg-amber-50/40" : c.reviewed ? "border-gray-200" : "border-dlh-greenMid"}`} data-testid={`admin-comment-${c.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-800">{c.author_name}</span>
                    <span className="text-xs text-gray-400">{formatWIT(c.created_at, true)}</span>
                    {c.parent_id && <span className="text-[11px] font-bold bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><CornerDownRight className="h-3 w-3" /> Balasan</span>}
                    {c.likes > 0 && <span className="text-[11px] font-bold bg-dlh-greenPale text-dlh-green px-2 py-0.5 rounded-full inline-flex items-center gap-1"><ThumbsUp className="h-3 w-3" /> {c.likes}</span>}
                    {!c.reviewed && <span className="text-[11px] font-bold bg-dlh-orange text-white px-2 py-0.5 rounded-full">Baru</span>}
                    {c.flagged && <span className="text-[11px] font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Terjaring filter</span>}
                    {c.status === "hidden" && <span className="text-[11px] font-bold bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">Disembunyikan</span>}
                  </div>
                  <Link to={`/berita/${c.article_slug}`} target="_blank" className="text-xs text-dlh-greenMid hover:text-dlh-orange inline-flex items-center gap-1 mt-0.5">
                    {c.article_title || c.article_slug} <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {!c.reviewed && <IconBtn onClick={() => markReviewed(c)} testid={`comment-reviewed-${c.id}`} title="Tandai sudah ditinjau"><CheckCheck className="h-4 w-4" /></IconBtn>}
                  {c.status === "visible" ? (
                    <IconBtn onClick={() => setStatus(c, "hidden")} testid={`comment-hide-${c.id}`} title="Sembunyikan"><EyeOff className="h-4 w-4" /></IconBtn>
                  ) : (
                    <IconBtn onClick={() => setStatus(c, "visible")} testid={`comment-show-${c.id}`} title="Tampilkan"><Eye className="h-4 w-4" /></IconBtn>
                  )}
                  <IconBtn onClick={() => openReply(c)} testid={`comment-reply-btn-${c.id}`} title="Balas"><Reply className="h-4 w-4" /></IconBtn>
                  <IconBtn onClick={() => remove(c)} testid={`comment-delete-${c.id}`} title="Hapus" danger><Trash2 className="h-4 w-4" /></IconBtn>
                </div>
              </div>

              <p className="text-sm text-gray-700 mt-2 whitespace-pre-line break-words">{c.body}</p>

              {c.reply && replyFor !== c.id && (
                <div className="mt-3 bg-dlh-greenPale border-l-4 border-dlh-green rounded-r-lg p-3">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-dlh-green mb-1"><ShieldCheck className="h-4 w-4" /> Tanggapan DLH{c.reply.by_name ? ` — ${c.reply.by_name}` : ""}</div>
                  <p className="text-sm text-gray-700 whitespace-pre-line break-words">{c.reply.body}</p>
                </div>
              )}

              {replyFor === c.id && (
                <div className="mt-3" data-testid={`comment-reply-editor-${c.id}`}>
                  <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={3} maxLength={1500} placeholder="Tulis tanggapan resmi DLH…" className="resize-none" />
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => saveReply(c)} data-testid={`comment-reply-save-${c.id}`} className="text-sm font-semibold bg-dlh-green text-white px-4 py-1.5 rounded-full hover:bg-dlh-greenDark">Simpan Tanggapan</button>
                    <button onClick={() => { setReplyFor(null); setReplyText(""); }} className="text-sm font-semibold text-gray-500 px-4 py-1.5 rounded-full hover:bg-gray-100">Batal</button>
                    {c.reply && <button onClick={async () => { setReplyText(""); try { await api.put(`/comments/${c.id}`, { reply: "" }); toast.success("Tanggapan dihapus"); setReplyFor(null); load(); } catch { toast.error("Gagal"); } }} data-testid={`comment-reply-remove-${c.id}`} className="text-sm font-semibold text-red-500 px-4 py-1.5 rounded-full hover:bg-red-50 ml-auto">Hapus Tanggapan</button>}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function IconBtn({ onClick, children, title, testid, danger }) {
  return (
    <button onClick={onClick} title={title} data-testid={testid}
      className={`p-2 rounded-lg border transition-colors ${danger ? "border-red-200 text-red-500 hover:bg-red-50" : "border-gray-200 text-gray-600 hover:bg-gray-100"}`}>
      {children}
    </button>
  );
}
